/*
 * LOFTfifty6 — 3D-stoelconfigurator
 *
 * Eigen bouw. De bezoeker kiest onderstel, kleur onderstel en stof; het model
 * wordt live samengesteld uit twee losse 3D-bestanden (kuip + onderstel) die
 * in hetzelfde assenstelsel staan, dus het onderstel landt vanzelf op de
 * goede plek. De stof is een textuur die over de kuip wordt gelegd.
 *
 * De prijs komt uit de meegeleverde prijstabel (adviesverkoopprijs van Moods).
 * Voor 'in winkelmand' wordt de bijbehorende Shopify-variant live opgezocht via
 * /products/<handle>.js, zodat er nergens een prijs dubbel wordt bijgehouden.
 *
 * Weergave: three.js (MIT). Wordt pas geladen als de configurator in beeld komt.
 */

/* three.js via esm.sh: de voorbeeldmodules (GLTFLoader, OrbitControls…)
   importeren 'three' als kale naam. esm.sh schrijft dat om naar een echte URL,
   zodat er geen import-map op de pagina nodig is — die zou kunnen botsen met
   het thema. Alle modules verwijzen naar dezelfde versie, dus er komt maar één
   kopie van three.js in het geheugen.
   De Draco-decoder is geen module maar een los wasm-bestand; die blijft op
   jsdelivr staan. */
const DRIE = 'https://esm.sh/three@0.180.0';
const DRACO_PAD = 'https://cdn.jsdelivr.net/npm/three@0.180.0/examples/jsm/libs/draco/';

/* ---------------------------------------------------------------- helpers */

const maak = (tag, klasse, ouder) => {
  const el = document.createElement(tag);
  if (klasse) el.className = klasse;
  if (ouder) ouder.appendChild(el);
  return el;
};

/** € 1.299,95 — Nederlandse notatie, zonder centen als die 0 zijn. */
const euro = (bedrag) =>
  '€ ' + bedrag.toLocaleString('nl-NL', {
    minimumFractionDigits: bedrag % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });

/** 'BEIGE 170' → 'Beige 170' */
const netjes = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

const sleutelVan = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

/** Zoekt een waarde in een lijst zonder op hoofdletters te struikelen en geeft
    de schrijfwijze uit het manifest terug — daar wordt verderop mee gezocht.
    Een gedeelde link wordt weleens met de hand ingekort of overgetypt. */
const uitLijst = (lijst, waarde) =>
  (lijst || []).find((x) => x.toLowerCase() === String(waarde ?? '').toLowerCase()) ?? null;

/* --------------------------------------------------------- uit de collectie */

/* Modellen die LOFT56 niet meer voert. Deze lijst staat hier en niet in het
   manifest, om de eenvoudige reden dat bouw-manifest.mjs ze er juist uít laat:
   in het manifest bestaan ze niet, dus daar valt niets aan te herkennen. Zonder
   deze lijst zou een oude link naar zo'n model hier terugvallen op de eerste de
   beste stoel, en denkt de bezoeker een Barcelona te bestellen terwijl hij naar
   een Nice kijkt.
   De sleutels zijn de model-id's van Moods, in beide schrijfwijzen die in het
   dealermateriaal voorkomen.
     Mood #95   uit de collectie, opgave eigenaar 27-07-2026
     Barcelona  uit de collectie, opgave eigenaar 29-07-2026 */
const VERVALLEN_MODELLEN = new Map([
  ['barcelona', 'Barcelona'],
  ['mood_95', 'Mood #95'],
  ['mood95', 'Mood #95'],
]);

/* Waar de bezoeker wél terechtkan. Beide vervallen modellen zijn eetkamerstoelen
   en die collectie bestaat in de winkel; verwijzen naar de zoekpagina of naar de
   voorpagina zou hem opnieuw laten zoeken. */
const VERVALLEN_COLLECTIE = '/collections/eetkamerstoelen-1';

/** Het label van een vervallen model, of null. */
const vervallenModel = (sleutel) =>
  (sleutel ? VERVALLEN_MODELLEN.get(sleutelVan(sleutel)) : null) ?? null;

/* ------------------------------------------------------------------- data */

class Gegevens {
  constructor(rauw, assetbasis) {
    Object.assign(this, rauw);
    /* In het thema staan alle bestanden plat in /assets, met de mapnaam in de
       bestandsnaam verwerkt (kuipen/Nice.glb → lf6-kuipen-Nice.glb). Zonder
       assetbasis (lokale proef) worden de paden gewoon relatief gebruikt. */
    this.assetbasis = assetbasis || null;
    /* De Shopify-varianten bestaan alleen voor uitvoeringen die op voorraad
       liggen. Deze index vertelt of de huidige samenstelling er zo een is, en
       met welk SKU we die variant kunnen opzoeken. */
    this.voorraadIndex = new Map();
    for (const v of this.voorraad || []) {
      this.voorraadIndex.set([v.model, v.stof, v.been, v.draai, v.kleur].join('|'), v.sku);
    }
    this.perCategorie = new Map();
    /* Een stofcollectie heet in de winkel soms anders dan in de catalogus van
       Moods: 'Glory' staat in Shopify als 'TexGlory'. Wélke naam dat is, komt
       als veld mee op elke stof in het manifest — bouw-manifest.mjs haalt hem
       uit naam-aliassen.json. Hieronder wordt hij alleen per categorie
       klaargezet. Zo staat er in dit bestand geen enkele naam hard, en tonen
       de knop, de samenvatting en de bestelling gegarandeerd dezelfde naam. */
    this.winkelnamen = new Map();
    for (const stof of this.stoffen) {
      if (!this.perCategorie.has(stof.categorie)) this.perCategorie.set(stof.categorie, []);
      this.perCategorie.get(stof.categorie).push(stof);
      if (!this.winkelnamen.has(stof.categorie)) {
        this.winkelnamen.set(stof.categorie, stof.winkelnaam || stof.categorie);
      }
    }
  }

  /** De naam waaronder een stofcollectie in de winkel staat. */
  winkelnaam(categorie) {
    return this.winkelnamen.get(categorie) || categorie;
  }

  /** Volledige URL van een bestand. */
  url(pad) {
    if (this.assetbasis) return this.assetbasis + 'lf6-' + pad.replace(/\//g, '-');
    return pad;
  }

  /** SKU van de voorraadvariant voor deze samenstelling, of null. */
  voorraadSku(model, stof, been, draai, kleur) {
    if (!model || !stof) return null;
    return this.voorraadIndex.get([model.sleutel, stof.sleutel, been, draai, kleur].join('|')) || null;
  }

  model(sleutel) {
    return this.modellen.find((m) => m.sleutel === sleutel);
  }

  /**
   * Prijs van een samenstelling, of null als die combinatie niet bestaat.
   * De sleutel volgt de opbouw van de Moods-prijslijst.
   */
  prijs(model, been, draai, categorie) {
    const cat = sleutelVan(categorie);
    let sleutel;
    if (!been) sleutel = `${model.prijsvoorvoegsel}_${cat}`;
    else if (been === 'Bureauonderstel') sleutel = `${model.prijsvoorvoegsel}_bureauonderstel_${cat}`;
    else sleutel = `${model.prijsvoorvoegsel}_${sleutelVan(been)}_${draai === 'vast' ? 'fixed' : 'rotation'}_${cat}`;
    const p = this.prijzen[sleutel];
    return p === undefined ? null : p;
  }
}

/* ----------------------------------------------------------------- viewer */

class Weergave {
  constructor(vlak, gegevens) {
    this.vlak = vlak;
    this.gegevens = gegevens;
    this.klaar = null;
    this.kuip = null;
    this.been = null;
    this.texturen = new Map();
      this.modellen = new Map();
  }

  async start() {
    if (this.klaar) return this.klaar;
    this.klaar = (async () => {
      const [THREE, { GLTFLoader }, { DRACOLoader }, { OrbitControls }, { EXRLoader }, { HorizontalBlurShader }, { VerticalBlurShader }] = await Promise.all([
        import(DRIE),
        import(`${DRIE}/examples/jsm/loaders/GLTFLoader.js`),
        import(`${DRIE}/examples/jsm/loaders/DRACOLoader.js`),
        import(`${DRIE}/examples/jsm/controls/OrbitControls.js`),
        import(`${DRIE}/examples/jsm/loaders/EXRLoader.js`),
        import(`${DRIE}/examples/jsm/shaders/HorizontalBlurShader.js`),
        import(`${DRIE}/examples/jsm/shaders/VerticalBlurShader.js`),
      ]);
      this.THREE = THREE;

      const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
      renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
      /* De neutrale tooncurve houdt de stofkleur dicht bij de echte kleur;
         de filmische (ACES) trekt lichte tinten naar grijs, waardoor een
         beige stof op het scherm wit lijkt. */
      renderer.toneMapping = THREE.NeutralToneMapping ?? THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 0.95;
      renderer.shadowMap.enabled = true;
      renderer.shadowMap.type = THREE.PCFSoftShadowMap;
      this.vlak.appendChild(renderer.domElement);
      this.renderer = renderer;

      const scene = new THREE.Scene();
      this.scene = scene;

      /* De studio-omgeving van Moods zelf, als HDRI. Die bepaalt hoe het licht
         van alle kanten op de stof valt en waar het metaal in weerspiegelt —
         veel overtuigender dan een paar losse lampen. Valt het bestand weg,
         dan zetten we een eenvoudige binnenruimte op zodat er altijd iets
         te zien is. */
      const pmrem = new THREE.PMREMGenerator(renderer);
      pmrem.compileEquirectangularShader();
      try {
        const hdri = await new EXRLoader().loadAsync(this.gegevens.url('omgeving/studio.exr'));
        hdri.mapping = THREE.EquirectangularReflectionMapping;
        scene.environment = pmrem.fromEquirectangular(hdri).texture;
        hdri.dispose();
        /* Het omgevingsbeeld vult in; het hoofdlicht bepaalt de vorm. Staat de
           omgeving te fel, dan verdwijnt het reliëf van de stof. */
        if ('environmentIntensity' in scene) scene.environmentIntensity = 0.68;
      } catch (e) {
        const { RoomEnvironment } = await import(`${DRIE}/examples/jsm/environments/RoomEnvironment.js`);
        scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      }

      this.bouwRuimte(THREE, scene);
      this.bouwContactschaduw(THREE, scene, HorizontalBlurShader, VerticalBlurShader);

      this.camera = new THREE.PerspectiveCamera(32, 1, 0.1, 50);
      this.camera.position.set(1.35, 1.05, 1.45);

      const bediening = new OrbitControls(this.camera, renderer.domElement);
      bediening.enableDamping = true;
      bediening.dampingFactor = 0.08;
      bediening.enablePan = false;
      bediening.minDistance = 0.9;
      bediening.maxDistance = 3.2;
      /* Tussen ruim boven ooghoogte en net boven de vloer. Recht van boven
         kijken levert geen bruikbaar beeld van een stoel op. */
      bediening.minPolarAngle = 0.62;
      bediening.maxPolarAngle = Math.PI / 2 - 0.04;   // niet onder de vloer door
      bediening.target.set(0, 0.42, 0);
      this.bediening = bediening;

      this.stoel = new THREE.Group();
      scene.add(this.stoel);

      const draco = new DRACOLoader().setDecoderPath(DRACO_PAD);
      this.loader = new GLTFLoader().setDRACOLoader(draco);
      this.texturenLader = new THREE.TextureLoader();

      this.meet();
      new ResizeObserver(() => this.meet()).observe(this.vlak);
      let laatsteFrame = 0;
      this.inBeeld = true;
      new IntersectionObserver(([invoer]) => {
        this.inBeeld = invoer.isIntersecting;
        if (this.inBeeld) renderer.render(scene, this.camera);
      }, { rootMargin: '160px' }).observe(this.vlak);
      renderer.setAnimationLoop((tijd) => {
        if (document.hidden || !this.inBeeld || tijd - laatsteFrame < 33) return;
        laatsteFrame = tijd;
        bediening.update();
        renderer.render(scene, this.camera);
      });
    })();
    return this.klaar;
  }

  /**
   * De ruimte waarin de stoel staat: een vloer die naar de randen toe
   * vervaagt, plus licht. De vloer krijgt geen zichtbare rand — hij lost op
   * in de achtergrond, zoals bij een fotostudio met een doorlopend doek.
   */
  bouwRuimte(THREE, scene) {
    /* Verloop van ondoorzichtig in het midden naar niets aan de rand. Zonder
       dit zie je de rand van het vlak liggen en valt de illusie weg. */
    const doek = document.createElement('canvas');
    doek.width = doek.height = 256;
    const ctx = doek.getContext('2d');
    const verloop = ctx.createRadialGradient(128, 128, 10, 128, 128, 128);
    verloop.addColorStop(0, '#ffffff');
    verloop.addColorStop(0.62, '#dcdcdc');
    verloop.addColorStop(1, '#000000');
    ctx.fillStyle = verloop;
    ctx.fillRect(0, 0, 256, 256);
    const vervaging = new THREE.CanvasTexture(doek);

    const vloer = new THREE.Mesh(
      new THREE.CircleGeometry(4, 64).rotateX(-Math.PI / 2),
      new THREE.MeshStandardMaterial({
        color: 0xf2efe9,
        roughness: 0.92,
        metalness: 0,
        alphaMap: vervaging,
        transparent: true,
        depthWrite: false,
      })
    );
    vloer.position.y = -0.002;   // net onder de poten, anders flikkert het
    vloer.receiveShadow = true;
    scene.add(vloer);
    this.vloer = vloer;

    /* Belichting als bij een meubelopname: één groot zijraam als hoofdlicht,
       een zachte invulling aan de schaduwzijde zodat die niet dichtloopt, en
       een tegenlicht dat de omtrek oplicht. Het omgevingsbeeld doet de rest. */
    const hoofd = new THREE.DirectionalLight(0xfff4e8, 2.9);
    hoofd.castShadow = true;
    hoofd.shadow.mapSize.set(4096, 4096);
    hoofd.shadow.bias = -0.0004;
    hoofd.shadow.normalBias = 0.012;
    if ('intensity' in hoofd.shadow) hoofd.shadow.intensity = 1;
    scene.add(hoofd);
    scene.add(hoofd.target);
    this.hoofdlicht = hoofd;

    /* Invullicht: koeler en zwak, tegenover het hoofdlicht. Werpt geen
       schaduw — een tweede schaduw zou het beeld juist onrustig maken. */
    const vul = new THREE.DirectionalLight(0xe8f0ff, 0.45);
    scene.add(vul);
    scene.add(vul.target);
    this.vullicht = vul;

    /* Tegenlicht van schuin achter: geeft de stof langs de rand een lichtrand,
       waardoor de stoel loskomt van de achtergrond. */
    const tegen = new THREE.DirectionalLight(0xfffaf2, 1.1);
    scene.add(tegen);
    scene.add(tegen.target);
    this.tegenlicht = tegen;
  }

  /**
   * Lichten en schaduwbereik afstemmen op het model dat er nu staat.
   *
   * De lampen staan op afstand naar verhouding van de stoel, en het
   * schaduwvenster wordt strak om het model gelegd. Dat laatste bepaalt de
   * scherpte: hoe kleiner het venster, hoe meer pixels van de schaduwkaart op
   * de stoel vallen. Een vast, ruim venster kost onnodig veel scherpte.
   */
  stelLichtIn(doos) {
    if (!this.hoofdlicht) return;
    const midden = doos.getCenter(new this.THREE.Vector3());
    const maat = doos.getSize(new this.THREE.Vector3());
    const straal = Math.max(maat.x, maat.y, maat.z) * 0.5;
    const mik = new this.THREE.Vector3(midden.x, doos.min.y + maat.y * 0.45, midden.z);

    const zet = (licht, richting, afstand) => {
      licht.position.copy(mik).addScaledVector(richting.normalize(), straal * afstand);
      licht.target.position.copy(mik);
      licht.target.updateMatrixWorld();
    };

    zet(this.hoofdlicht, new this.THREE.Vector3(-0.75, 1.35, 0.95), 6);
    zet(this.vullicht, new this.THREE.Vector3(1.25, 0.55, 0.85), 6);
    zet(this.tegenlicht, new this.THREE.Vector3(0.35, 0.9, -1.3), 6);

    /* Schaduwvenster strak om de stoel, met een kleine marge. */
    const s = this.hoofdlicht.shadow.camera;
    const rand = straal * 1.25;
    s.left = -rand; s.right = rand;
    s.bottom = -rand; s.top = rand;
    s.near = straal * 1.5;
    s.far = straal * 12;
    s.updateProjectionMatrix();
    this.hoofdlicht.shadow.needsUpdate = true;
  }

  /**
   * Contactschaduw: de donkere aanzet vlak onder de poten, waar het licht
   * niet meer bij kan. Dat is wat een model op de vloer laat 'landen'.
   *
   * We fotograferen de stoel van onderaf met een dieptemateriaal, vervagen dat
   * beeld tweemaal, en leggen het als textuur op de vloer. Dit gebeurt alleen
   * bij een wisseling — de stoel beweegt niet, dus elke beeldwissel opnieuw
   * berekenen zou verspilling zijn.
   */
  bouwContactschaduw(THREE, scene, HorizontalBlurShader, VerticalBlurShader) {
    const MAAT = 2.4;
    const HOOGTE = 1.1;      // tot hoe hoog boven de vloer meetelt

    const groep = new THREE.Group();
    scene.add(groep);

    /* 1024 in plaats van 512: de poten zijn dun, en op een halve resolutie
       wordt hun afdruk een vlek in plaats van een vorm. */
    this.doel = new THREE.WebGLRenderTarget(1024, 1024);
    this.doel.texture.generateMipmaps = false;
    this.doelVaag = new THREE.WebGLRenderTarget(1024, 1024);
    this.doelVaag.texture.generateMipmaps = false;

    const vlak = new THREE.PlaneGeometry(MAAT, MAAT).rotateX(Math.PI / 2);
    const schaduw = new THREE.Mesh(vlak, new THREE.MeshBasicMaterial({
      map: this.doel.texture,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
    }));
    schaduw.renderOrder = 2;
    schaduw.scale.y = -1;            // de textuur komt gespiegeld uit de camera
    schaduw.position.y = 0.001;
    groep.add(schaduw);
    this.schaduwVlak = schaduw;

    this.vaagVlak = new THREE.Mesh(vlak);
    this.vaagVlak.visible = false;
    groep.add(this.vaagVlak);

    this.schaduwCamera = new THREE.OrthographicCamera(-MAAT / 2, MAAT / 2, MAAT / 2, -MAAT / 2, 0, HOOGTE);
    this.schaduwCamera.rotation.x = Math.PI / 2;   // van onderaf omhoog kijken
    groep.add(this.schaduwCamera);

    /* Als MeshDepthMaterial, maar van zwart naar doorzichtig in plaats van
       zwart naar wit — zo is het meteen bruikbaar als schaduwtextuur. */
    const diepte = new THREE.MeshDepthMaterial();
    diepte.userData.donkerte = { value: 2.2 };
    diepte.onBeforeCompile = (shader) => {
      shader.uniforms.donkerte = diepte.userData.donkerte;
      shader.fragmentShader = 'uniform float donkerte;\n' + shader.fragmentShader.replace(
        'gl_FragColor = vec4( vec3( 1.0 - fragCoordZ ), opacity );',
        'gl_FragColor = vec4( vec3( 0.0 ), ( 1.0 - fragCoordZ ) * donkerte );'
      );
    };
    diepte.depthTest = false;
    diepte.depthWrite = false;
    this.diepteMateriaal = diepte;

    this.vaagH = new THREE.ShaderMaterial(HorizontalBlurShader);
    this.vaagH.depthTest = false;
    this.vaagV = new THREE.ShaderMaterial(VerticalBlurShader);
    this.vaagV.depthTest = false;
  }

  /** Eén vervagingsronde over de schaduwtextuur, horizontaal en verticaal. */
  vervaag(mate) {
    const r = this.renderer;
    this.vaagVlak.visible = true;

    this.vaagVlak.material = this.vaagH;
    this.vaagH.uniforms.tDiffuse.value = this.doel.texture;
    this.vaagH.uniforms.h.value = mate / 256;
    r.setRenderTarget(this.doelVaag);
    r.render(this.vaagVlak, this.schaduwCamera);

    this.vaagVlak.material = this.vaagV;
    this.vaagV.uniforms.tDiffuse.value = this.doelVaag.texture;
    this.vaagV.uniforms.v.value = mate / 256;
    r.setRenderTarget(this.doel);
    r.render(this.vaagVlak, this.schaduwCamera);

    this.vaagVlak.visible = false;
  }

  /** Schaduw opnieuw berekenen; aanroepen na elke wisseling van model of onderstel. */
  ververContactschaduw() {
    if (!this.doel) return;
    const r = this.renderer;
    const alpha = r.getClearAlpha();
    /* De vloer en de schaduw zelf horen niet op de diepte-opname: die zouden
       zichzelf verduisteren. */
    this.vloer.visible = false;
    this.schaduwVlak.visible = false;
    this.scene.overrideMaterial = this.diepteMateriaal;
    r.setClearAlpha(0);
    r.setRenderTarget(this.doel);
    r.clear();
    r.render(this.scene, this.schaduwCamera);
    this.scene.overrideMaterial = null;
    /* Twee rondes: een brede voor de zachte aanzet, een smalle die de vorm
       vlak onder de poten weer aanscherpt. */
    this.vervaag(3.4);
    this.vervaag(1.1);
    r.setRenderTarget(null);
    r.setClearAlpha(alpha);
    this.vloer.visible = true;
    this.schaduwVlak.visible = true;
  }

  meet() {
    const b = this.vlak.getBoundingClientRect();
    if (!b.width || !b.height) return;
    this.renderer.setSize(b.width, b.height, false);
    const nieuw = b.width / b.height;
    const veranderd = Math.abs((this.camera.aspect || 0) - nieuw) > 0.05;
    this.camera.aspect = nieuw;
    this.camera.updateProjectionMatrix();
    /* Gaat het venster van liggend naar staand, dan valt de stoel anders half
       buiten beeld; opnieuw kaderen, met behoud van de kijkhoek. */
    if (veranderd && this.gekaderd) this.kader();
  }

  async textuur(pad, kleurruimte) {
    if (!this.texturen.has(pad)) {
      const t = await this.texturenLader.loadAsync(this.gegevens.url(pad));
      t.wrapS = t.wrapT = this.THREE.RepeatWrapping;
      t.colorSpace = kleurruimte || this.THREE.NoColorSpace;
      t.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      this.texturen.set(pad, t);
    }
    return this.texturen.get(pad);
  }

  async laadDeel(pad) {
    if (!this.modellen.has(pad)) {
        this.modellen.set(pad, this.loader.loadAsync(this.gegevens.url(pad)).then((gltf) => gltf.scene));
      }
      const bron = await this.modellen.get(pad);
      const deel = bron.clone(true);
    deel.traverse((n) => {
      if (n.isMesh) { n.castShadow = true; n.receiveShadow = true; }
    });
    return deel;
  }

  /** De kuip is het deel met de bekleding; die herkennen we aan het materiaal. */
  static isBekleding(materiaal) {
    return /MeshPhysicalMaterial/i.test(materiaal.name || '');
  }
  static isOnderstel(materiaal) {
    return /MeshStandardMaterial/i.test(materiaal.name || '');
  }

  async zetModel(model) {
    await this.start();
    if (this.kuip) { this.stoel.remove(this.kuip); this.kuip = null; }
    const map = model.soort === 'compleet' ? 'compleet/' : 'kuipen/';
    this.kuip = await this.laadDeel(map + model.bestand);
    this.stoel.add(this.kuip);
  }

  async zetOnderstel(bestand) {
    await this.start();
    const wissel = (this.onderstelWissel || 0) + 1;
    this.onderstelWissel = wissel;
    const vorig = this.been;

    if (!bestand) {
      if (vorig) this.stoel.remove(vorig);
      this.been = null;
      return;
    }

    const nieuw = await this.laadDeel('onderstellen/' + bestand);
    if (wissel !== this.onderstelWissel) return;

    /* Ieder los onderstel heeft een iets ander nulpunt. Leg daarom de echte
       bovenzijde tegen de echte onderzijde van de kuip, met een minieme overlap
       zodat er vanuit geen enkele kijkhoek een lichtspleet zichtbaar blijft. */
    const kuipDoos = this.kuip ? new this.THREE.Box3().setFromObject(this.kuip) : null;
    const beenDoos = new this.THREE.Box3().setFromObject(nieuw);
    let doelY = nieuw.position.y;
    if (kuipDoos && !kuipDoos.isEmpty() && !beenDoos.isEmpty()) {
      const bureauCorrectie = /bureau/i.test(bestand) ? 0.05 : 0;
      doelY += kuipDoos.min.y - beenDoos.max.y + 0.008 + bureauCorrectie;
    }

    const materialen = [];
    const gezien = new Set();
    nieuw.traverse((n) => {
      if (!n.isMesh) return;
      const lijst = Array.isArray(n.material) ? n.material : [n.material];
      for (const materiaal of lijst) {
        if (!materiaal || gezien.has(materiaal)) continue;
        gezien.add(materiaal);
        materialen.push({
          materiaal,
          opacity: materiaal.opacity,
          transparent: materiaal.transparent,
        });
      }
    });

    const minderBeweging = matchMedia('(prefers-reduced-motion: reduce)').matches;
    nieuw.position.y = doelY;
    const kuipY = this.kuip ? this.kuip.position.y : 0;
    this.stoel.add(nieuw);
    this.been = nieuw;

    if (minderBeweging) {
      if (vorig) this.stoel.remove(vorig);
      this.ververContactschaduw();
      return;
    }

    for (const item of materialen) {
      item.materiaal.transparent = true;
      item.materiaal.opacity = item.opacity * 0.72;
    }

    await new Promise((klaar) => {
      const begin = performance.now();
      let oudVerwijderd = false;
      const stap = (nu) => {
        const tijd = Math.min(1, (nu - begin) / 220);
        const gemak = 1 - Math.pow(1 - tijd, 3);
        if (this.kuip) this.kuip.position.y = kuipY + 0.012 * (1 - gemak);
        for (const item of materialen) item.materiaal.opacity = item.opacity * gemak;

        if (!oudVerwijderd && tijd >= 0.28) {
          if (vorig) this.stoel.remove(vorig);
          oudVerwijderd = true;
        }

        if (tijd < 1) {
          requestAnimationFrame(stap);
          return;
        }

        nieuw.position.y = doelY;
        if (this.kuip) this.kuip.position.y = kuipY;
        for (const item of materialen) {
          item.materiaal.opacity = item.opacity;
          item.materiaal.transparent = item.transparent;
        }
        if (!oudVerwijderd && vorig) this.stoel.remove(vorig);
        this.ververContactschaduw();
        klaar();
      };
      requestAnimationFrame(stap);
    });
  }

  /**
   * Instellingen per stofsoort. 'Sheen' is de zachte glans die je bij stof
   * langs de randen ziet, waar het licht door de opstaande vezels strijkt.
   * Zonder die glans oogt bekleding als geverfd plastic. Fluweel heeft die
   * glans sterk en gericht; geweven stof veel zwakker en diffuser.
   */
  static STOFSOORT = {
    velvet: { sheen: 1, sheenRuw: 0.28, ruw: 0.66, relief: 0.75 },
    tops:   { sheen: 0.6, sheenRuw: 0.72, ruw: 0.9, relief: 1.15 },
  };

  async zetStof(stof) {
    await this.start();
    if (!this.kuip) return;
    const soort = Weergave.STOFSOORT[stof.materiaal] || Weergave.STOFSOORT.tops;

    const [kleur, normaal] = await Promise.all([
      this.textuur(`stoffen/textuur/${stof.sleutel}.webp`, this.THREE.SRGBColorSpace),
      this.textuur(`stoffen/normaal/${stof.sleutel}.webp`),
    ]);

    /* De glans neemt de kleur van de stof aan, maar lichter — bij fluweel is
       die randglans nooit puur wit en nooit zo donker als de stof zelf. */
    const [r, g, b] = stof.rgb || [200, 195, 185];
    const sheenKleur = new this.THREE.Color(r / 255, g / 255, b / 255)
      .convertSRGBToLinear()
      .lerp(new this.THREE.Color(1, 1, 1), 0.45);

    const textuurRotatie = sleutelVan(stof.categorie) === 'be_stylish' ? 0 : Math.PI / 2;

    this.kuip.traverse((n) => {
      if (!n.isMesh) return;
      for (const mat of (Array.isArray(n.material) ? n.material : [n.material])) {
        if (!Weergave.isBekleding(mat)) continue;

        /* Elke stof heeft zijn eigen herhaling; een kloon houdt de gedeelde
           textuur bruikbaar voor andere modellen op de pagina. */
        const t = kleur.clone();
        t.center.set(0.5, 0.5);
        t.rotation = textuurRotatie;
        t.needsUpdate = true;
        t.repeat.set(stof.herhaling, stof.herhaling);
        mat.map = t;

        const nm = normaal.clone();
        nm.center.set(0.5, 0.5);
        nm.rotation = textuurRotatie;
        nm.needsUpdate = true;
        nm.repeat.set(stof.herhaling, stof.herhaling);
        mat.normalMap = nm;
        mat.normalScale.set(soort.relief, soort.relief);

        mat.color.setRGB(1, 1, 1);
        mat.roughness = soort.ruw;
        mat.metalness = 0;
        mat.envMapIntensity = stof.glans;

        /* Sheen bestaat alleen op MeshPhysicalMaterial; de kuipen gebruiken
           dat, dus de eigenschap is er. */
        if ('sheen' in mat) {
          mat.sheen = soort.sheen;
          mat.sheenRoughness = soort.sheenRuw;
          mat.sheenColor.copy(sheenKleur);
        }
        mat.needsUpdate = true;
      }
    });
  }

  async zetOnderstelkleur(soort, kleur, herhaling, metaalheid) {
    await this.start();
    /* Meestal is het onderstel een apart model. Bij Malmo en Malmo Wood zit het
       vast aan de kuip; dan kleuren we het onderstel-materiaal binnen de kuip. */
    const doelen = [this.been, this.kuip].filter(Boolean);
    if (!doelen.length) return;
    const naam = `${soort}-${kleur.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`;
    const [diffuse, ruwheid] = await Promise.all([
      this.textuur(`onderstelkleuren/${naam}-diffuse.webp`, this.THREE.SRGBColorSpace),
      this.textuur(`onderstelkleuren/${naam}-roughness.webp`),
    ]);
    for(const doel of doelen)doel.traverse((n) => {
      if (!n.isMesh) return;
      for (const mat of (Array.isArray(n.material) ? n.material : [n.material])) {
        if (!Weergave.isOnderstel(mat)) continue;
        const d = diffuse.clone(); d.needsUpdate = true; d.repeat.set(herhaling, herhaling);
        const r = ruwheid.clone(); r.needsUpdate = true; r.repeat.set(herhaling, herhaling);
        mat.map = d;
        mat.roughnessMap = r;
        mat.metalness = metaalheid;
        mat.needsUpdate = true;
      }
    });
  }

  /** Camera zo zetten dat de hele stoel netjes in beeld staat. */
  kader() {
    if (!this.THREE) return;
    const doos = new this.THREE.Box3().setFromObject(this.stoel);
    if (doos.isEmpty()) return;
    const midden = doos.getCenter(new this.THREE.Vector3());
    const maat = doos.getSize(new this.THREE.Vector3());
    const straal = Math.max(maat.x, maat.y, maat.z) * 0.5;
    /* Iets ruimer dan strikt nodig, zodat de stoel niet tegen de randen plakt.
       Bij een staand vlak (telefoon) is het beeld horizontaal smaller dan de
       kijkhoek suggereert; daarom extra afstand naarmate het vlak smaller is. */
    const staand = Math.min(1, this.camera.aspect || 1);
    const afstand = ((straal / Math.sin((this.camera.fov * Math.PI) / 360)) * 1.4) / staand;

    /* Bij een herberekening (draaien van de telefoon, breder venster) houden we
       de hoek aan waar de bezoeker naar kijkt; alleen bij het eerste model
       vallen we terug op de standaardhoek.

       Die standaardhoek is een schuin vooraanzicht net iets boven zithoogte —
       zoals een meubelfoto. De y-waarde is bewust laag: kijk je van boven, dan
       zie je vooral de zitting en verdwijnt de vorm van rug en armleuning. */
    const huidig = this.camera.position.clone().sub(this.bediening.target);
    const richting = this.gekaderd && huidig.lengthSq() > 0
      ? huidig.normalize()
      : new this.THREE.Vector3(0.58, 0.21, 0.79).normalize();
    this.gekaderd = true;

    /* Iets onder het midden mikken, zodat de stoel wat hoger in beeld staat
       en de schaduw op de vloer zichtbaar blijft. */
    const mik = new this.THREE.Vector3(midden.x, doos.min.y + maat.y * 0.45, midden.z);
    this.bediening.target.copy(mik);
    this.camera.position.copy(mik).addScaledVector(richting, afstand);
    this.bediening.minDistance = afstand * 0.25;
    this.bediening.maxDistance = afstand * 2.2;
    this.bediening.update();

    this.stelLichtIn(doos);
  }
}

/* ------------------------------------------------------------------ paneel */

class Configurator {
  constructor(houder) {
    this.houder = houder;
    this.vastModel = houder.dataset.model || null;
    this.handle = houder.dataset.product || null;
    this.keuze = { model: null, been: null, draai: null, kleursoort: 'metaal', kleur: 'zwart', stof: null };
    this.varianten = null;
  }

  async start() {
    const bron = this.houder.dataset.gegevens;
    this.data = new Gegevens(await (await fetch(bron)).json(), this.houder.dataset.assets);
    this.houder._lf3dInstance = this;
    this.houder.addEventListener('lf3d:select-sku', (event) => {
      this.kiesVoorraadSku(event.detail?.sku);
    });

    const gedeeld = this.adresKeuzes();
    /* Op een productpagina bepaalt het product zelf het model. Een 'model' in de
       adresbalk wordt daar genegeerd: de bezoeker zou anders op de pagina van de
       ene stoel een andere te zien krijgen, terwijl de winkelmandknop zijn
       variant nog steeds bij dít product opzoekt. Uitdrukkelijke eis van de
       eigenaar. Alleen op een overzichtspagina telt de parameter mee. */
    const gevraagd = this.vastModel || gedeeld.model || null;

    /* Eerst kijken of het gevraagde model nog bestaat. De regel hieronder zou
       anders stilzwijgend op Nice terugvallen, en dan toont een gedeelde link
       naar een vervallen model doodgemoedereerd een andere stoel. */
    const vervallen = vervallenModel(gevraagd);
    if (vervallen) {
      this.toonBericht(
        `${vervallen} is uit de collectie`,
        `De ${vervallen} wordt niet meer gevoerd en is daarom ook niet meer in 3D samen te stellen.`
      );
      return;
    }
    if (gevraagd && !this.data.model(gevraagd)) {
      /* Geen vervallen model, maar wel een dat wij niet in 3D hebben — een
         barkruk of een poef bijvoorbeeld, of een sleutel uit een oude link. */
      this.toonBericht(
        'Niet in 3D samen te stellen',
        'Van dit model is geen 3D-uitvoering beschikbaar.'
      );
      return;
    }

    this.bouwOpmaak();
    const start =
      this.data.model(gevraagd) ||
      this.data.modellen.find((m) => m.label === 'Nice') ||
      this.data.modellen[0];
    await this.kiesModel(start, true);
    await this.herstelUitAdres(gedeeld);
  }

  /* ---- gedeelde link -------------------------------------------------- */

  /**
   * De samenstelling zoals die in de adresbalk staat: de link die als regel
   * 'Configuratie' met de bestelling meegaat en die klanten onderling delen.
   * Hier wordt alleen gelezen — of een onderdeel nog bestaat, wordt pas
   * beoordeeld op het moment dat het wordt toegepast.
   */
  adresKeuzes() {
    const uit = {};
    let vraag;
    try {
      vraag = new URL(location.href).searchParams;
    } catch (e) {
      return uit;   // geen bruikbaar adres; dan gewoon de standaardstoel
    }
    for (const naam of ['model', 'been', 'draai', 'kleur', 'stof']) {
      const waarde = (vraag.get(naam) || '').trim();
      if (waarde) uit[naam] = waarde;
    }
    /* De 3D-configurator van Moods zelf had voor Malmo geen tweede model, maar
       een losse vlag in de URL. Links van vóór onze eigen configurator dragen
       die nog; zonder deze omzetting laat zo'n link een Malmo in stof zien waar
       een houten bedoeld was. Het is de enige plek waar die vlag nog voorkomt. */
    if (uit.model === 'malmo' && vraag.get('malmoHout') === 'true') uit.model = 'malmo_wood';
    return uit;
  }

  /**
   * Zet de gedeelde samenstelling terug, ná kiesModel() — die heeft dan al een
   * volledige, geldige stoel neergezet.
   *
   * Elk onderdeel wordt apart tegen het manifest gehouden. Een link kan maanden
   * oud zijn: verdwijnt er een stof of een onderstel uit de collectie, dan hoort
   * de rest van de keuze overeind te blijven in plaats van dat de hele link
   * niets meer doet.
   */
  async herstelUitAdres(gedeeld) {
    const model = this.keuze.model;
    if (!model) return;

    const onderstellen = model.onderstellen || {};
    const been = uitLijst(Object.keys(onderstellen), gedeeld.been);
    if (been) {
      /* Onderstel en draaibaarheid horen bij elkaar: een Iras bestaat alleen
         draaibaar. Vraagt de link een uitvoering die dít onderstel niet kent,
         dan houden we het onderstel aan en nemen we de uitvoering die het wel
         heeft — één achterhaald woord hoort niet het hele onderstel te kosten. */
      const draai = uitLijst(onderstellen[been], gedeeld.draai) || onderstellen[been][0];
      if (this.keuze.been !== been || this.keuze.draai !== draai) {
        if (this.keuze.draai !== draai) {
          /* Het gevraagde onderstel er alvast in zetten: kiesDraaibaarheid()
             houdt een onderstel aan dat in de nieuwe rij nog bestaat, en laadt
             dan meteen het goede in plaats van eerst een ander dat we een
             tel later weer weggooien. */
          this.keuze.been = been;
          await this.kiesDraaibaarheid(draai);
        }
        /* Afwachten: elk onderstel is een eigen 3D-bestand, en wie die laadbeurten
           door elkaar laat lopen houdt het onderstel over dat toevallig het
           laatst binnenkwam. */
        if (this.keuze.been !== been) await this.kiesOnderstel(been, draai);
      }
    } else {
      /* Geen (bekend) onderstel in de link, maar wel een uitvoering: dan alleen
         die toepassen en het onderstel laten kiezen dat daarbij hoort. */
      const draai = uitLijst(this.draaibaarheden(), gedeeld.draai);
      if (draai && this.keuze.draai !== draai) await this.kiesDraaibaarheid(draai);
    }

    /* De kleur hoort bij het onderstel dat er nú onder staat — ook bij Malmo,
       waar de poot vastzit maar wel in kleur gekozen wordt. */
    const huidig = this.huidigOnderstel();
    const kleur = huidig ? uitLijst(huidig.kleuren, gedeeld.kleur) : null;
    if (kleur && this.keuze.kleur !== kleur) {
      this.keuze.kleur = kleur;
      await this.pasOnderstelkleurToe();
    }

    if (gedeeld.stof) {
      const stof = this.data.stoffen.find((s) => s.sleutel === gedeeld.stof);
      /* Een stof zonder prijs voor deze samenstelling is niet te bestellen; die
         zetten we dus ook niet terug. De stof die kiesModel() koos blijft dan
         staan, mét een prijs die wel klopt. */
      if (stof && stof !== this.keuze.stof && this.prijsVan(stof) !== null) {
        this.vulCategorieen(stof.categorie);
        await this.kiesStof(stof);
      }
    }
  }

  /**
   * Wat er in plaats van de configurator komt te staan als het gevraagde model
   * niet (meer) samengesteld kan worden. Bewust zonder prijs, zonder 3D-beeld en
   * zonder knop: er valt niets te bestellen, en een knop 'op maat aanvragen' zou
   * de indruk wekken dat het model alsnog te krijgen is.
   */
  toonBericht(kop, tekst) {
    this.houder.classList.add('lf3d', 'lf3d--bericht');
    this.houder.innerHTML = '';
    const blok = maak('div', 'lf3d__bericht', this.houder);
    maak('h3', 'lf3d__bericht-kop', blok).textContent = kop;
    maak('p', 'lf3d__bericht-tekst', blok).textContent = tekst;
    const link = maak('a', 'lf3d__bericht-link', blok);
    link.href = VERVALLEN_COLLECTIE;
    link.textContent = 'Bekijk de eetkamerstoelen';
  }

  /* ---- opmaak --------------------------------------------------------- */

  bouwOpmaak() {
    this.houder.classList.add('lf3d');
    this.houder.innerHTML = '';

    const beeld = maak('div', 'lf3d__beeld', this.houder);
    this.vlak = maak('div', 'lf3d__vlak', beeld);
    this.laadmelding = maak('p', 'lf3d__laden', beeld);
    this.laadmelding.textContent = 'Model laden…';
    const hint = maak('p', 'lf3d__hint', beeld);
    maak('span', 'lf3d__hint-muis', hint).textContent = 'Sleep om te draaien · scroll om in te zoomen';
    maak('span', 'lf3d__hint-vinger', hint).textContent = 'Veeg om te draaien · knijp om in te zoomen';

    const paneel = maak('div', 'lf3d__paneel', this.houder);

    if (!this.vastModel) {
      this.modelRij = this.sectie(paneel, 'Model');
      for (const m of this.data.modellen) {
        const knop = this.tegel(this.modelRij, m.armsteun === 'zonder' && this.heeftTweeArmVarianten(m)
          ? `${m.label} zonder armleuning`
          : m.label);
        knop.classList.add('lf3d__tegel--tekst');
        knop.addEventListener('click', () => this.kiesModel(m));
        knop._model = m;
      }
    }

    this.typeRij = this.sectie(paneel, 'Type onderstel');
    this.beenRij = this.sectie(paneel, 'Onderstel');
    this.kleurRij = this.sectie(paneel, 'Kleur onderstel');
    this.categorieRij = this.sectie(paneel, 'Stof');
    this.stofRij = maak('div', 'lf3d__rij lf3d__rij--stof', this.categorieRij.parentElement);

    /* Onderaan blijft staan wat de klant bestelt en wat het kost. */
    const voet = maak('div', 'lf3d__voet', this.houder);
    this.samenvatting = maak('p', 'lf3d__samenvatting', voet);
    /* De omschrijving in een eigen span, anders wist het bijwerken ervan het
       voorraadlabel dat ernaast staat. */
    this.samenvattingTekst = maak('span', null, this.samenvatting);
    this.voorraadmelding = maak('span', 'lf3d__voorraad', this.samenvatting);
    /* Gaat het bestellen niet door, dan hoort de bezoeker te lezen wat er nog
       ontbreekt; een knop die stilletjes niets doet laat hem raden. Dezelfde
       pil-opmaak als het voorraadlabel, zodat er geen extra stijlregel bij
       hoeft. Verbergen gebeurt via display en niet via het hidden-attribuut:
       .lf3d__voorraad zet display zelf, en die klasse wint. */
    this.melding = maak('span', 'lf3d__voorraad lf3d__melding', this.samenvatting);
    this.melding.setAttribute('role', 'status');
    this.melding.style.display = 'none';
    const rechts = maak('div', 'lf3d__voet-rechts', voet);
    this.prijsveld = maak('span', 'lf3d__prijs', rechts);
    this.bestelknop = maak('button', 'lf3d__bestel', rechts);
    this.bestelknop.type = 'button';
    this.bestelknop.textContent = 'In winkelmand';
    this.bestelknop.addEventListener('click', () => this.bestel());

    this.weergave = new Weergave(this.vlak, this.data);
  }

  heeftTweeArmVarianten(m) {
    return this.data.modellen.filter((x) => x.label === m.label).length > 1;
  }

  sectie(ouder, titel) {
    const blok = maak('div', 'lf3d__blok', ouder);
    maak('h3', 'lf3d__kop', blok).textContent = titel;
    return maak('div', 'lf3d__rij', blok);
  }

  tegel(rij, label, afbeelding) {
    const knop = maak('button', 'lf3d__tegel', rij);
    knop.type = 'button';
    knop.title = label;
    knop.setAttribute('aria-label', label);
    if (afbeelding) {
      const img = maak('img', null, knop);
      img.src = afbeelding;
      img.alt = '';
      img.loading = 'lazy';
    }
    maak('span', 'lf3d__tegel-naam', knop).textContent = label;
    return knop;
  }

  /* ---- keuzes --------------------------------------------------------- */

  async kiesModel(model, eerste) {
    this.keuze.model = model;
    if (this.modelRij) {
      for (const k of this.modelRij.children) k.classList.toggle('is-gekozen', k._model === model);
    }

    this.bezig(true);
    await this.weergave.zetModel(model);

    /* Afwachten, want vulOnderstellen() kiest zelf al een onderstel en laadt
       daarvoor een 3D-bestand. Liep dat nog terwijl er daarna iets anders werd
       gekozen — door de bezoeker of door een gedeelde link — dan bleef het
       onderstel staan dat het laatst binnenkwam in plaats van het gekozene. */
    await this.vulOnderstellen();
    this.vulOnderstelkleuren();
    await this.kiesEersteGeldigeStof();

    this.weergave.kader();
    this.weergave.ververContactschaduw();
    this.bezig(false);
    if (eerste) this.varianten = null;
  }

  /* Het onderstel wordt in twee stappen gekozen, net als bij Moods zelf:
     eerst draaibaar of vast, daarna het model onderstel. Draaibare en vaste
     onderstellen door elkaar tonen klopt niet — een Iras bestaat alleen
     draaibaar, een Earth en een Orion alleen vast. */

  /** Welke draaibaarheden heeft dit model? */
  draaibaarheden() {
    const alle = new Set();
    for (const draaien of Object.values(this.keuze.model.onderstellen)) {
      for (const d of draaien) alle.add(d);
    }
    return ['draai', 'vast'].filter((d) => alle.has(d));
  }

  async vulOnderstellen() {
    const model = this.keuze.model;
    const soorten = this.draaibaarheden();

    /* Malmo en Malmo Wood hebben geen keuze: daar zit het onderstel vast aan
       de kuip. Beide blokken blijven dan weg. */
    this.typeRij.parentElement.hidden = soorten.length === 0;
    this.beenRij.parentElement.hidden = soorten.length === 0;
    if (!soorten.length) {
      this.keuze.been = null;
      this.keuze.draai = null;
      await this.weergave.zetOnderstel(null);
      this.vulOnderstelkleuren();
      await this.pasOnderstelkleurToe();
      return;
    }

    this.typeRij.innerHTML = '';
    for (const d of soorten) {
      const knop = maak('button', 'lf3d__chip', this.typeRij);
      knop.type = 'button';
      knop.textContent = d === 'vast' ? 'Vast' : 'Draaibaar';
      knop._draai = d;
      knop.addEventListener('click', () => this.kiesDraaibaarheid(d));
    }

    /* Alleen als de huidige draaibaarheid niet meer bestaat, terugvallen. */
    const draai = soorten.includes(this.keuze.draai) ? this.keuze.draai : soorten[0];
    return this.kiesDraaibaarheid(draai);
  }

  /** Draaibaar of vast; daarna alleen de onderstellen die daarbij horen.
      Geeft het laden van het gekozen onderstel terug, zodat wie er meteen een
      andere keuze achteraan doet daarop kan wachten. */
  kiesDraaibaarheid(draai) {
    this.keuze.draai = draai;
    for (const k of this.typeRij.children) k.classList.toggle('is-gekozen', k._draai === draai);

    /* Zelfde volgorde als bij Moods, zodat wie beide kent hetzelfde rijtje ziet. */
    const VOLGORDE = ['Venus', 'Earth', 'Iras', 'Atlas', 'Mars', 'Moon', 'Orion', 'Sirius', 'Bureauonderstel'];
    const benen = Object.entries(this.keuze.model.onderstellen)
      .filter(([, draaien]) => draaien.includes(draai))
      .map(([been]) => been)
      .sort((a, b) => VOLGORDE.indexOf(a) - VOLGORDE.indexOf(b));

    this.beenRij.innerHTML = '';
    for (const been of benen) {
      /* De miniatuur heet naar het onderstel zelf, niet naar het 3D-bestand:
         'Bureauonderstel_draai.webp' hoort bij 'Bureau_draai.glb'. */
      /* 'Bureauonderstel' past niet op één regel onder de tegel. Met een
         onzichtbare breekkans na 'Bureau' valt hij netjes op het samengestelde
         woord uiteen in plaats van middenin. De naam zelf blijft heel. */
      const label = been.replace('Bureauonderstel', 'Bureau\u200Bonderstel');
      const knop = this.tegel(this.beenRij, label, this.data.url(`knoppen/onderstel/${been}_${draai}.webp`));
      knop.title = been;
      knop.setAttribute('aria-label', been);
      knop.addEventListener('click', () => this.kiesOnderstel(been, draai));
      knop._been = been;
    }

    const been = benen.includes(this.keuze.been) ? this.keuze.been : benen[0];
    return been ? this.kiesOnderstel(been, draai) : Promise.resolve();
  }

  async kiesOnderstel(been, draai) {
    this.keuze.been = been;
    this.keuze.draai = draai;
    for (const k of this.beenRij.children) k.classList.toggle('is-gekozen', k._been === been);

    this.bezig(true);
    await this.weergave.zetOnderstel(this.data.onderstellen[been].bestanden[draai]);
    /* Elk onderstel heeft zijn eigen kleuren: Earth is van hout en kent
       naturel, zwart en bruin; het bureauonderstel bestaat alleen in zwart. */
    this.vulOnderstelkleuren();
    await this.pasOnderstelkleurToe();
    /* Ander onderstel betekent een andere afdruk op de vloer. */
    this.weergave.ververContactschaduw();
    this.werkStofbeschikbaarheidBij();
    this.werkVoetBij();
    this.bezig(false);
  }

  /** Het onderstel waarvan de kleur nu telt — ook bij modellen waar het
      onderstel vast aan de kuip zit (Malmo staat op een Earth-poot). */
  huidigOnderstel() {
    const naam = this.keuze.been || this.keuze.model?.vastOnderstel;
    return naam ? { naam, ...this.data.onderstellen[naam] } : null;
  }

  vulOnderstelkleuren() {
    const rij = this.kleurRij;
    rij.innerHTML = '';
    const been = this.huidigOnderstel();

    /* Bij één kleur valt er niets te kiezen; dan slaan we het blok over. */
    rij.parentElement.hidden = !been || been.kleuren.length < 2;
    if (!been) return;

    for (const kleur of been.kleuren) {
      const bestand = `${been.materiaal}-${kleur.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.webp`;
      const knop = this.tegel(rij, `${netjes(been.materiaal)} ${kleur.toLowerCase()}`, this.data.url('knoppen/onderstelkleur/' + bestand));
      knop.classList.add('lf3d__tegel--rond');
      knop.addEventListener('click', () => { this.keuze.kleur = kleur; this.pasOnderstelkleurToe(); });
      knop._kleur = kleur;
    }

    /* Kleur van het vorige onderstel bestaat hier misschien niet; dan de eerste. */
    if (!been.kleuren.includes(this.keuze.kleur)) this.keuze.kleur = been.kleuren[0];
    this.keuze.kleursoort = been.materiaal;
  }

  async pasOnderstelkleurToe() {
    const been = this.huidigOnderstel();
    if (!been) return;
    for (const k of this.kleurRij.children) {
      if (!k._kleur) continue;
      k.classList.toggle('is-gekozen', k._kleur === this.keuze.kleur);
    }
    /* De herhaling en de metaalheid horen bij het materiaal: metaal is fijner
       geschaald en spiegelt, hout niet. */
    const instelling = this.data.onderstelkleuren[been.materiaal] || {};
    const render = instelling.render || {};
    const perKleur = (render.colors || {})[this.keuze.kleur.replace(/\s+/g, '_')] || {};
    await this.weergave.zetOnderstelkleur(
      been.materiaal,
      this.keuze.kleur,
      render.repeat || 6,
      perKleur.metalness ?? (been.materiaal === 'hout' ? 0 : 0.4)
    );
    this.werkVoetBij();
  }

  /* ---- stof ----------------------------------------------------------- */

  async kiesEersteGeldigeStof() {
    const eerste = this.data.stoffen.find((s) => this.prijsVan(s) !== null) || this.data.stoffen[0];
    this.vulCategorieen(eerste.categorie);
    await this.kiesStof(eerste);
  }

  vulCategorieen(actief) {
    const rij = this.categorieRij;
    rij.innerHTML = '';
    for (const categorie of this.data.perCategorie.keys()) {
      const knop = maak('button', 'lf3d__chip', rij);
      knop.type = 'button';
      /* Op de knop de winkelnaam, in _categorie de catalogusnaam: de bezoeker
         zoekt 'TexGlory' terug in de webshop, maar de prijstabel en de
         voorraadlijst kennen alleen 'Glory'. */
      knop.textContent = this.data.winkelnaam(categorie);
      knop._categorie = categorie;
      knop.addEventListener('click', () => this.vulStoffen(categorie));
    }
    this.vulStoffen(actief);
  }

  /** Hoe een stof heet zodra de bezoeker hem ziet: altijd de winkelnaam, zodat
      staal, samenvatting en orderregel elkaar niet tegenspreken. */
  stofnaam(stof) {
    return `${this.data.winkelnaam(stof.categorie)} ${netjes(stof.kleur)}`;
  }

  vulStoffen(categorie) {
    for (const k of this.categorieRij.children) k.classList.toggle('is-gekozen', k._categorie === categorie);
    const rij = this.stofRij;
    rij.innerHTML = '';
    for (const stof of this.data.perCategorie.get(categorie)) {
      const knop = this.tegel(rij, this.stofnaam(stof), this.data.url(`stoffen/staal/${stof.sleutel}.webp`));
      knop.classList.add('lf3d__tegel--rond');
      knop._stof = stof;
      knop.addEventListener('click', () => this.kiesStof(stof));
    }
    this.werkStofbeschikbaarheidBij();
  }

  /** Een stof die voor deze samenstelling geen prijs heeft, kan niet besteld
      worden; die zetten we uit in plaats van er een prijs bij te verzinnen. */
  werkStofbeschikbaarheidBij() {
    for (const k of this.stofRij.children) {
      if (!k._stof) continue;
      const kan = this.prijsVan(k._stof) !== null;
      k.disabled = !kan;
      k.classList.toggle('is-uit', !kan);
      k.title = kan ? this.stofnaam(k._stof) : 'Deze stof is niet leverbaar in deze samenstelling';
      k.classList.toggle('is-gekozen', k._stof === this.keuze.stof);
    }
  }

  prijsVan(stof) {
    const m = this.keuze.model;
    if (!m) return null;
    return this.data.prijs(m, this.keuze.been, this.keuze.draai, stof.categorie);
  }

  async kiesStof(stof) {
    if (this.prijsVan(stof) === null) return;
    this.keuze.stof = stof;
    this.werkStofbeschikbaarheidBij();
    this.bezig(true);
    await this.weergave.zetStof(stof);
    this.bezig(false);
    this.werkVoetBij();
  }

  /* ---- voet ----------------------------------------------------------- */

  /** 'Zwart' bij metaal, 'Hout naturel' bij hout — anders is op de order niet
      te zien welk materiaal er besteld is. */
  kleurOmschrijving() {
    const been = this.huidigOnderstel();
    if (!been) return '';
    return been.materiaal === 'hout'
      ? `${netjes(been.materiaal)} ${this.keuze.kleur.toLowerCase()}`
      : netjes(this.keuze.kleur);
  }

  werkVoetBij() {
    /* Elke nieuwe keuze maakt een eerdere waarschuwing achterhaald. */
    this.meld('');
    const { model, been, draai, kleur, stof } = this.keuze;
    if (!model || !stof) return;

    const delen = [model.label];
    if (this.heeftTweeArmVarianten(model)) delen.push(model.armsteun === 'met' ? 'met armleuning' : 'zonder armleuning');
    if (been) delen.push(`${been} ${draai === 'vast' ? 'vast' : 'draaibaar'}`);
    {
      /* Bij Earth kan de klant kiezen tussen metaal en hout; dan moet de
         uitvoering erbij, anders zegt 'Zwart' niet genoeg. */
      const kleurTekst = this.kleurOmschrijving();
      if (kleurTekst) delen.push(kleurTekst);
    }
    delen.push(this.stofnaam(stof));
    this.samenvattingTekst.textContent = delen.join(' · ');

    const prijs = this.prijsVan(stof);
    this.prijsveld.textContent = prijs === null ? '' : euro(prijs);

    /* Voorraad en maatwerk gebruiken allebei de bestaande Shopify-prijsvariant.
       Het voorraadnummer blijft als bestelregel-eigenschap meegaan. */
    this.huidigeSku = this.data.voorraadSku(model, stof, been, draai, this.keuze.kleur);
    this.bestelknop.textContent = 'In winkelmand';
    this.bestelknop.classList.remove('lf3d__bestel--aanvraag');
    this.bestelknop.disabled = prijs === null;
    if (this.voorraadmelding) {
      /* Termijnen bevestigd door de eigenaar (28-07-2026): voorraaduitvoeringen
         tonen alleen dat ze op voorraad liggen, zonder termijn; maatwerk krijgt
         de bevestigde levertijd van 10 tot 14 weken. */
      this.voorraadmelding.textContent = this.huidigeSku ? 'Op voorraad' : 'Levertijd 10-14 weken';
      this.voorraadmelding.className = 'lf3d__voorraad' + (this.huidigeSku ? ' is-voorraad' : '');
    }
  }

  bezig(aan) {
    this.houder.classList.toggle('is-bezig', aan);
    this.laadmelding.hidden = !aan;
  }

  /** Korte melding naast de samenvatting. Zonder tekst verdwijnt hij weer, en
      hij dooft vanzelf zodat er bij een volgende keuze niets ouds blijft staan. */
  meld(tekst) {
    if (!this.melding) return;
    clearTimeout(this.meldingTimer);
    this.melding.textContent = tekst || '';
    this.melding.style.display = tekst ? '' : 'none';
    if (tekst) this.meldingTimer = setTimeout(() => this.meld(''), 8000);
  }

  /* ---- bestellen ------------------------------------------------------ */

  /**
   * De huidige samenstelling als deelbare link. De sleutels zijn dezelfde als
   * in this.keuze, zodat het terugzetten van een gedeelde configuratie later
   * één op één gaat en er geen tweede vertaaltabel bijkomt.
   */
  configuratieLink() {
    const { model, been, draai, stof } = this.keuze;
    const vraag = new URLSearchParams();
    if (model) vraag.set('model', model.sleutel);
    if (been) vraag.set('been', been);
    if (draai) vraag.set('draai', draai);
    /* Ook zonder onderstelkeuze is er een kleur: Malmo staat op een vaste
       Earth-poot die de klant nog wel in kleur kiest. */
    if (this.keuze.kleur && this.huidigOnderstel()) vraag.set('kleur', this.keuze.kleur);
    if (stof) vraag.set('stof', stof.sleutel);
    return `${location.origin}${location.pathname}?${vraag}`;
  }

  /**
   * De regels die als line-item properties meegaan naar de winkelmand: wat er
   * op de orderbevestiging en de pakbon moet staan, in de volgorde die de
   * eigenaar heeft vastgelegd.
   *
   * Een regel die niet van toepassing is blijft weg. Een lege waarde of
   * 'n.v.t.' op de order kost de leverancier een navraag, en suggereert dat er
   * een keuze is gemaakt die niet bestaat.
   */

  configuratieCode() {
    const { model, been, draai, stof } = this.keuze;
    const deel = (waarde) => String(waarde || '')
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .trim().toUpperCase().replace(/[^A-Z0-9]+/g, '-').replace(/^-|-$/g, '');
    return [
      model?.sleutel || model?.label,
      been,
      draai === 'vast' ? 'vast' : 'draaibaar',
      this.kleurOmschrijving(),
      stof ? this.data.winkelnaam(stof.categorie) : '',
      stof?.kleur,
      stof?.code,
    ].map(deel).filter(Boolean).join('-');
  }

  eigenschappen() {
    const { model, been, draai, stof } = this.keuze;
    const regels = {};
    if (model) regels['Model'] = model.label;
    /* Alleen Lisboa en London bestaan met én zonder armleuning; bij de rest zou
       deze regel een keuze suggereren die er niet is. */
    if (model && this.heeftTweeArmVarianten(model)) {
      regels['Uitvoering'] = model.armsteun === 'met' ? 'met armleuning' : 'zonder armleuning';
    }
    if (stof) {
      /* De naam die de klant op de site zag, niet de catalogusnaam van Moods —
         anders staat er 'Glory' op de bevestiging van een 'TexGlory'-stoel. */
      regels['Stofcollectie'] = this.data.winkelnaam(stof.categorie);
      regels['Stofkleur'] = netjes(stof.kleur);
      if (stof.code) regels['Stofcode'] = String(stof.code);
    }
    /* Malmo en Malmo Wood hebben geen onderstelkeuze; hun poot zit vast aan de
       kuip en hoort dus niet als losse regel op de order. De kleur ervan wel,
       want die kiest de klant nog steeds. */
    if (been) regels['Onderstel'] = been;
    const kleurTekst = this.kleurOmschrijving();
    if (kleurTekst) regels['Kleur onderstel'] = kleurTekst;
    if (been && draai) regels['Type onderstel'] = draai === 'vast' ? 'vast' : 'draaibaar';
    regels['Artikelnummer'] = this.huidigeSku || this.configuratieCode();
    regels['Configuratie'] = this.configuratieLink();
    return regels;
  }

  /**
   * Welke verplichte regels nog leeg zijn. Liever niets versturen dan een order
   * zonder stof of onderstel: die kan niemand uitleveren en komt als vraag
   * terug bij de klant.
   */
  ontbrekendeKeuzes() {
    const eigen = this.eigenschappen();
    const nodig = ['Model', 'Stofcollectie', 'Stofkleur', 'Artikelnummer'];
    /* Alleen modellen met een echte onderstelkeuze hoeven onderstel en kleur te
       hebben; bij Malmo bestaat die keuze niet en zou de controle altijd falen. */
    if (this.keuze.model && this.draaibaarheden().length) {
      nodig.push('Onderstel', 'Kleur onderstel', 'Type onderstel');
    }
    return nodig.filter((naam) => !String(eigen[naam] || '').trim());
  }

  /**
   * De Shopify-variant die bij deze samenstelling hoort, opgezocht op SKU.
   * Alleen voorraaduitvoeringen bestaan als variant; op SKU zoeken is exact,
   * waar zoeken op optienamen zou gaan raden.
   */

  async kiesVoorraadSku(sku) {
    const gezocht = String(sku || '').trim().toUpperCase();
    const regel = [...this.data.voorraadIndex.entries()]
      .find(([, waarde]) => String(waarde || '').trim().toUpperCase() === gezocht);
    if (!regel) {
      this.meld('Deze voorraadvariant kan niet in de configurator worden geladen.');
      return false;
    }
    const [modelSleutel, stofSleutel, been, draai, kleur] = regel[0].split('|');
    const model = this.data.model(modelSleutel);
    const stof = this.data.stoffen.find((item) => item.sleutel === stofSleutel);
    if (!model || !stof) return false;
    if (this.keuze.model !== model) await this.kiesModel(model, false);
    await this.herstelUitAdres({ been, draai, kleur, stof: stofSleutel });
    const variant = await this.zoekVariant(sku);
    if (variant?.price != null) this.prijsveld.textContent = euro(variant.price / 100);
    this.houder.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return true;
  }

  async zoekVariant(sku = '') {
    if (!this.handle) throw new Error('DEBUG producthandle ontbreekt.');
    if (!this.varianten) {
      const antwoord = await fetch('/products/' + this.handle + '.js');
      if (!antwoord.ok) return null;
      this.varianten = (await antwoord.json()).variants;
    }

    const gezocht = String(sku || '').trim().toUpperCase();
    if (gezocht) {
      const voorraad = this.varianten.find((v) => v.available && String(v.sku || '').trim().toUpperCase() === gezocht);
      if (voorraad) return voorraad;
    }

    const { been, draai, stof } = this.keuze;
    if (!been || !stof) throw new Error('DEBUG variantinvoer ontbreekt: been=' + been + ', stof=' + (stof?.sleutel || 'leeg'));
    const uitvoering = draai === 'vast' ? 'vast' : 'draaibaar';
    const stofcategorie = this.data.winkelnaam(stof.categorie);
    const titel = (been + ' ' + uitvoering + ' / ' + stofcategorie).trim().toLocaleLowerCase('nl-NL');
    const titelMatches = this.varianten.filter((v) => String(v.title || '').trim().toLocaleLowerCase('nl-NL') === titel);
    this.meld('DEBUG ' + JSON.stringify({ titel, matches: titelMatches.map((v) => ({ id: v.id, title: v.title, price: v.price, available: v.available })), eerste: this.varianten.slice(0, 3).map((v) => v.title) }));
    if (!titelMatches.length) throw new Error('DEBUG geen titelmatch: ' + titel + ' | eerste: ' + this.varianten.slice(0, 3).map((v) => v.title).join(' || '));
    const koopbaar = titelMatches.find((v) => v.available);
    if (!koopbaar) throw new Error('DEBUG niet beschikbaar: ' + JSON.stringify(titelMatches.map((v) => ({ id: v.id, price: v.price, available: v.available }))));
    return koopbaar;
  }

  async bestel() {
    this.meld('');
    const ontbreekt = this.ontbrekendeKeuzes();
    if (ontbreekt.length) {
      this.meld('Maak eerst een keuze voor: ' + ontbreekt.join(', ').toLowerCase() + '.');
      return;
    }

    this.bestelknop.disabled = true;
    const oud = this.bestelknop.textContent;
    this.bestelknop.textContent = 'Even geduld…';
    try {
      const variant = await this.zoekVariant(this.huidigeSku);
      if (!variant) throw new Error('Deze configuratie is nog niet gekoppeld aan een koopbare variant.');
      const prijs = this.prijsVan(this.keuze.stof);
      if (prijs === null || Number(variant.price) !== Math.round(prijs * 100)) {
        throw new Error('De winkelmandprijs wijkt af. Deze configuratie is niet toegevoegd.');
      }
      const antwoord = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties: this.eigenschappen() }] }),
      });
      if (!antwoord.ok) throw new Error('Toevoegen aan de winkelmand is niet gelukt.');
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      this.bestelknop.textContent = 'Toegevoegd';
      setTimeout(() => { this.bestelknop.textContent = oud; this.bestelknop.disabled = false; }, 2200);
    } catch (e) {
      this.meld(e?.message || 'Toevoegen aan de winkelmand is niet gelukt.');
      this.bestelknop.textContent = 'Probeer opnieuw';
      setTimeout(() => { this.bestelknop.textContent = oud; this.bestelknop.disabled = false; }, 2600);
    }
  }
}

/* ----------------------------------------------------------------- opstart */

/* Pas laden als de configurator in beeld komt: three.js en de modellen zijn
   samen enkele megabytes, en die hoeven niet mee met het openen van de pagina. */
function wacht(houder) {
  if (houder._lf3d) return;
  houder._lf3d = true;
  const kijker = new IntersectionObserver((regels) => {
    for (const regel of regels) {
      if (!regel.isIntersecting) continue;
      kijker.disconnect();
      new Configurator(houder).start().catch((e) => {
        houder.innerHTML = '<p class="lf3d__fout">De configurator kan nu niet worden geladen. Probeer het later nog eens.</p>';
        console.error('[lf3d]', e);
      });
    }
  }, { rootMargin: '300px' });
  kijker.observe(houder);
}

for (const el of document.querySelectorAll('[data-lf3d]')) wacht(el);

document.addEventListener('shopify:section:load', () => {
  for (const el of document.querySelectorAll('[data-lf3d]')) wacht(el);
});
